import { useEffect, useState } from 'react';
import { UserDetails } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { useMutation } from 'react-query';
import { DevicesApi } from '@frontend/api-devices';
import { useTranslation } from '@frontend/i18n';
import { DropdownField, FormRow, Text, TextField, useDebouncedValue, useFormField } from '@frontend/design-system';
import { type CreateSoftphonePropTypes, inputFieldStyles } from '..';
import { logError } from '../../../../utils/loggers';

type Props = CreateSoftphonePropTypes & {
  errorMessage: string;
  setErrorMessage: (val: string) => void;
  usersWithoutSoftphones: UserDetails[];
};

export const FirstStep = ({
  getFieldProps,
  tenantId,
  errorMessage,
  setErrorMessage,
  usersWithoutSoftphones,
}: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'create-new-softphone' });

  const [value, setValue] = useState(getFieldProps('extension').value);
  const debouncedSearch = useDebouncedValue(value);

  const { mutate: mutateExtensionAvailability } = useMutation(
    (extensionNumber: string) => DevicesApi.checkExtensionAvailability({ extensionNumber, tenantId: tenantId ?? '' }),
    {
      onSuccess: ({ availability, message }) => {
        if (availability === 'IN_USE' || availability === 'FORBIDDEN') {
          setErrorMessage(message);
        } else {
          if (!!errorMessage) setErrorMessage('');
          getFieldProps('extension').onChange({ name: 'extension', value });
        }
      },
      onError: (error) => {
        logError('FirstStep:mutateExtensionAvailability | Failed to check extension availability', {
          data: { tenantId, extensionNumber: value, error },
        });
      },
    }
  );

  const extensionFieldProps = useFormField(
    {
      required: true,
      type: 'text',
      value,
      validator: () => errorMessage,
      validateOnChange: true,
    },
    [errorMessage]
  );

  useEffect(() => {
    if (debouncedSearch) {
      mutateExtensionAvailability(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    setValue(extensionFieldProps.value);
  }, [extensionFieldProps.value]);

  return (
    <>
      <Text>{t('Select the user who will be assigned the softphone device and choose an extension number.')}</Text>
      <FormRow css={inputFieldStyles}>
        <DropdownField
          {...getFieldProps('userID')}
          onChange={(e) => {
            const oldValue = getFieldProps('userID').value;
            const newValue = e.value;
            if (e.value !== oldValue) {
              getFieldProps('deviceName').onChange({ name: 'deviceName', value: '' });
              getFieldProps('internalIDName').onChange({ name: 'internalIDName', value: '' });
            }
            getFieldProps('userID').onChange({ name: 'userID', value: newValue });
          }}
          label={t('Select a User')}
        >
          {usersWithoutSoftphones.map((user) => {
            return user.userId ? (
              <DropdownField.Option key={user.userId} value={user.userId}>
                {user.firstName + ' ' + user.lastName}
              </DropdownField.Option>
            ) : null;
          })}
        </DropdownField>
      </FormRow>
      <FormRow css={inputFieldStyles}>
        <TextField
          name={'Extension Number'}
          helperText={t('Choose an extension number that is not currently in use.')}
          label={t('Extension Number')}
          maxLength={6}
          {...extensionFieldProps}
          onKeyDown={(e) => {
            setValue(e.currentTarget.value);
          }}
        />
      </FormRow>
    </>
  );
};
